import { useId, useState } from "react";
import { Button } from "@fortex/map";
import type { AppLabels } from "../../i18n";

export type IconImage = { base64: string; width: number; height: number };

export function dataUrlOf(base64: string | null | undefined): string | null {
  if (!base64) return null;

  return base64.startsWith("data:") ? base64 : `data:image/png;base64,${base64}`;
}

export function base64OfDataUrl(dataUrl: string): string {
  const index = dataUrl.indexOf(",");

  return index >= 0 ? dataUrl.substring(index + 1) : dataUrl;
}

export function readImageFile(file: Blob): Promise<IconImage> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onerror = () => reject(new Error("read failed"));
    reader.onload = () => {
      const dataUrl = String(reader.result ?? "");
      const image = new Image();

      image.onerror = () => reject(new Error("not an image"));
      image.onload = () => resolve({ base64: base64OfDataUrl(dataUrl), width: image.naturalWidth, height: image.naturalHeight });
      image.src = dataUrl;
    };
    reader.readAsDataURL(file);
  });
}

export type IconFieldProps = {
  label: string;
  value: string | null;
  labels: AppLabels;
  onChange: (image: IconImage | null) => void;
};

export function IconField({ label, value, labels, onChange }: IconFieldProps) {
  const id = useId();
  const [error, setError] = useState<string | null>(null);
  const preview = dataUrlOf(value);

  return (
    <div className="fxmap-field">
      <span className="fxmap-field-label">{label}</span>
      <div className="fxapp-icon-upload">
        <span className="fxapp-marker-preview" aria-label={labels.iconPreview} role="img">
          {preview !== null ? <img src={preview} alt="" /> : null}
        </span>
        <input
          id={id}
          type="file"
          accept="image/*"
          className="fxmap-visually-hidden"
          onChange={event => {
            const file = event.target.files?.[0];

            event.target.value = "";

            if (!file) return;

            readImageFile(file)
              .then(image => {
                setError(null);
                onChange(image);
              })
              .catch(() => setError(labels.iconInvalid));
          }}
        />
        <label htmlFor={id} className="fxmap-btn fxmap-btn-ghost">
          <span className="fxmap-btn-label">{labels.iconUpload}</span>
        </label>
        {preview !== null ? <Button kind="text" size="sm" icon="trash" label={labels.iconRemove} onClick={() => onChange(null)} /> : null}
      </div>
      {error !== null ? (
        <p className="fxmap-field-error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
